/**
 * Enquête n°3 — Adapté de l'Étude de Cas CCS n°22
 * (Formation initiale moto : sensibiliser à la fatigue et ses conséquences)
 *
 * Sources : INSERM 2022 (Williamson "Fatigue equals alcohol"), ONISR 2024,
 * Sécurité routière, Académie du Sommeil 2023.
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const fatigueMotoEnquete = {
  slug: 'fatigue-moto',
  themeCode: 'L',
  issueNumber: 3,
  totalIssues: 8,
  series: 'enquetes',
  category: 'Enquête physiologique',
  shortTitle: 'Fatigue moto',
  title: "Dormir, c'est rouler : la fatigue à moto",
  kicker: 'Enquête n°3 · Série « Témoignages & Sécurité »',
  subtitle:
    "17 heures sans dormir, c'est l'équivalent de 0,5 g/L d'alcool dans le sang. 24 heures : 1 g/L. Pourtant aucun radar ne flashe la fatigue. Et 1 motard sur 5 mort sur la route avait simplement besoin de sommeil.",
  heroImage:
    'https://images.unsplash.com/photo-1763142185874-e79de466536a?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt: 'Motard fatigué sur autoroute, lumière crépusculaire',
  readTimeMin: 14,
  seoDescription:
    "Enquête sur la fatigue à moto en 2026 : neurosciences du sommeil, microsiestes 12 sec, dette de sommeil, 7 réflexes pour ne pas mourir au guidon. Conforme au programme officiel et au cadre ECSR.",
  seoKeywords: 'fatigue moto, microsieste, dette de sommeil moto, somnolence guidon, fatigue accident motard',

  edito: {
    title: 'Le motard qui s\'endort ne le sait pas',
    body: "C'est l'illusion la plus dangereuse du 2-roues : <strong>tu crois rester en alerte parce que tu sens le vent</strong>. C'est faux. Le cerveau peut entrer en <em>microsommeil</em> (3 à 30 secondes d'inconscience totale) tout en gardant les yeux ouverts. À 130 km/h, 5 secondes d'absence = <strong>180 mètres parcourus</strong> sans pilote conscient. <br/><br/>L'ONISR estime que la fatigue est <strong>cause directe de 17 à 22 % des accidents mortels moto</strong>, soit environ 130 morts/an en France. Toujours sous-diagnostiquée (ça ne se voit pas sur un alcootest), elle reste l'ennemi invisible du motard. Cette enquête te dit ce qui se passe dans ton cerveau et ce que tu peux faire concrètement.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Le microsommeil : 12 secondes qui tuent',
      paragraphs: [
        "Le microsommeil est un épisode bref (3 à 30 secondes) où le cerveau passe involontairement en sommeil <em>tout en gardant les yeux ouverts</em>. Pendant cet épisode, le motard ne réagit plus aux stimuli extérieurs. Il continue tout droit.",
        "<strong>Durée moyenne</strong> : 12 secondes. <strong>À 130 km/h</strong> : 433 mètres parcourus sans contrôle. <strong>À 80 km/h</strong> : 267 mètres. C'est <strong>plus que la longueur d'un avion gros porteur</strong> sans pilote conscient.",
        "<strong>Quand survient-il ?</strong> Pic entre <strong>2h et 6h du matin</strong> (creux du rythme circadien) et entre <strong>13h et 16h</strong> (creux post-prandial). En conditions monotones (autoroute, ligne droite) même en plein jour si dette de sommeil préalable.",
        "<strong>Indicateurs avant-coureurs</strong> : bâillements répétés, paupières lourdes, yeux qui piquent ou pleurent, sensation de « décalage », oubli des derniers kilomètres parcourus (<em>fugues amnésiques routières</em>). <strong>Si tu te dis « je ne me souviens plus du dernier village traversé » : arrête-toi immédiatement</strong>.",
      ],
      keyPoints: [
        'Microsommeil = 12 sec moyenne, yeux ouverts',
        'À 130 km/h = 433 mètres sans contrôle',
        'Pic 2h-6h et 13h-16h',
        'Fugue amnésique = signal d’alerte rouge',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "L'étude Williamson (Australie, 2000) a comparé fatigue et alcool sur 39 conducteurs en simulateur. Résultats : <strong>17 heures sans dormir = 0,5 g/L d'alcool dans le sang</strong> en termes de performance réaction/coordination/vigilance. <strong>24 heures sans dormir = 1 g/L</strong> (le double du seuil légal). Sauf que la loi ne te flashe que sur l'alcool. La fatigue est aussi mortelle, mais juridiquement invisible. Le Code de la route français (art. R412-6) impose seulement « d'être constamment en état et en position d'exécuter commodément et sans délai toutes les manœuvres » — disposition floue rarement appliquée.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'La dette de sommeil : ennemi invisible',
      paragraphs: [
        "Un adulte a besoin de <strong>7 à 9 heures de sommeil par 24h</strong>. Si tu dors 6h pendant 5 nuits, tu accumules <strong>5 à 15 heures de dette de sommeil</strong>. Et cette dette ne se rembourse pas en une grasse mat dimanche.",
        "<strong>Effet documentsé</strong> : avec 4 heures de dette, ta vigilance est réduite de 30 %. Avec 8 heures, de 50 %. Tu ne le sens pas — c'est précisément le piège. Le cortex préfrontal qui devrait te dire « je suis fatigué » est lui-même le premier touché par la fatigue (effet de seuil).",
        "<strong>Profil-type fatal</strong> : motard de retour d'une semaine de travail (lundi-vendredi 7h de sommeil), qui part en balade le samedi très tôt (5h, dette = 5h), fait un trajet long (300 km), et fait son virage de retour vers 14h (creux post-prandial + dette). Statistiquement, c'est là que tu meurs.",
        "<strong>Café, Red Bull : le faux ami</strong>. La caféine bloque les récepteurs à l'adénosine (molécule qui te donne envie de dormir). Mais elle ne supprime pas le besoin de sommeil — elle le masque. Effet : 3 à 5 heures, puis <strong>« crash de caféine »</strong> avec endormissement soudain encore plus dangereux qu'avant.",
      ],
      keyPoints: [
        '7-9h de sommeil/24h pour adulte',
        'Dette 4h = vigilance -30 %',
        'Cortex préfrontal = 1er touché → tu ne sens pas',
        'Café = masque, pas solution',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Je vais ouvrir grand la visière pour me réveiller. » <strong>FAUX et dangereux.</strong> Le froid masque la fatigue par stimulation cutanée mais ne supprime pas le microsommeil. Pareil pour : musique fort dans le casque, mordre l'intérieur de sa joue, gifles auto-administrées. Ces astuces sont des <em>palliatifs</em> qui retardent l'aveu — et qui tuent quand même. <strong>Seule solution véritable : dormir.</strong> Une sieste de 20 min sur un parking d'autoroute = 2h de vigilance retrouvée. C'est l'investissement qui sauve.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: '7 réflexes anti-fatigue pour motard',
      paragraphs: [
        "Synthèse des recommandations INRS + Sécurité routière + Académie du Sommeil :",
        "<strong>1. Dors au moins 7h la veille d'un trajet > 200 km</strong>. Si tu as moins de 6h — reporte ou annule. Aucune excuse.",
        "<strong>2. Pause toutes les 2 heures, systématique</strong>. Même si tu te sens bien. La fatigue ne se voit pas — elle se prévient.",
        "<strong>3. Évite les créneaux 2h-6h et 13h-16h</strong>. Ce sont les pires moments physiologiques. Roule plutôt 8h-12h ou 16h-20h.",
        "<strong>4. La sieste flash de 20 min</strong>. Sur un parking, sieste de 15-20 min (pas plus, sinon réveil difficile par inertie). Aussi efficace que 3h de sommeil pour la vigilance immédiate.",
        "<strong>5. Hydratation et léger</strong>. La déshydratation accélère la fatigue. Bois régulièrement (eau, pas sodas). Mange léger : un sandwich gros + décalage glycémique = somnolence garantie.",
        "<strong>6. Ne roule pas seul sur les longs trajets</strong>. Le motard solo s'endort plus souvent que le motard en groupe (effet d'éveil mutuel par le bruit, la communication intercom, les arrêts forcés).",
        "<strong>7. La règle ultime — écoute ton corps</strong>. Premier bâillement = vigilance à -20 %. Deuxième bâillement = arrêt dans les 10 min. Troisième = arrêt IMMÉDIAT. Pas négociable.",
      ],
      keyPoints: [
        '1. Min 7h sommeil avant trajet long',
        '2. Pause /2h systématique',
        '3. Évite 2h-6h et 13h-16h',
        '4. Sieste flash 20 min = 3h sommeil',
        '7. 3 bâillements = arrêt immédiat',
      ],
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Quand un élève me dit « je vais conduire 8h ce week-end », je lui dis : <strong>fais un test simple le matin du départ</strong>. Tiens-toi en équilibre statique sur une jambe, yeux fermés, pendant 30 secondes. Si tu vacilles avant 20 secondes, tu n'es pas en état de monter sur ta moto. Ce test (Romberg test, utilisé en médecine du sport) mesure ton équilibre cérébeleux — directement impacté par la fatigue. C'est mieux qu'un alcootest pour la fatigue, et c'est gratuit. »",
    },
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « Je ne me souviens plus du tout du dernier kilomètre »",
      body: "« 41 ans, motard depuis 22 ans, je rentrais d'un week-end Mortagne-Paris (220 km, A11). On avait peu dormi avec ma compagne, on tait partis tard. 16h, autoroute, ligne droite, je rentre dans la file de droite pour souffler. <strong>Je me souviens encore d'un panneau à 26 km de la maison. Après, blanc total.</strong> Je me suis réveillé sur la bande d'arrêt d'urgence, debout sur ma moto qui frottait le rail. J'ai roulé 5 km sans aucun souvenir. Ma compagne hurlait dans l'intercom — elle ne m'a jamais réveillé parce que je gardais les yeux ouverts. <br/><br/>J'ai compris ce jour-là que mourir à moto, ça peut être simplement de s'être couché trop tard. Aujourd'hui, en dessous de 7h de sommeil la veille, je ne sors pas la moto. C'est devenu ma règle non-négociable. » <em>— Émilien, 41 ans</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '17h',
    middle: '=  0,5 g/L',
    label: "sans sommeil équivaut au seuil légal d'alcool",
    source: 'Williamson & Feyer, Sleep deprivation vs alcohol, 2000',
    accent: '#0891b2',
  },

  glossary: [
    { term: 'Microsommeil', def: "Épisode de sommeil involontaire de 3 à 30 secondes pendant lequel le cerveau ne traite plus les stimuli. Souvent les yeux restent ouverts." },
    { term: 'Dette de sommeil', def: 'Cumul des heures de sommeil manquantes par rapport au besoin physiologique. Affecte la vigilance proportionnellement.' },
    { term: 'Cortex préfrontal', def: 'Zone du cerveau gérant la planification et l’auto-évaluation. Premier touché par la fatigue — d’où l’incapacité à reconnaître sa propre somnolence.' },
    { term: 'Rythme circadien', def: 'Horloge interne biologique de 24h. Pics de fatigue à 2h-6h et 13h-16h.' },
    { term: 'Adénosine', def: 'Molécule qui s’accumule pendant l’éveil et déclenche la sensation de fatigue. Bloquée temporairement par la caféine.' },
    { term: 'Fugue amnésique routière', def: 'Trou de mémoire conscient des derniers kilomètres parcourus. Signal d’alerte rouge de microsommeil antérieur.' },
    { term: 'Crash de caféine', def: 'Endormissement brutal après la levée de l’effet stimulant de la caféine (3-5h après ingestion).' },
    { term: 'Sieste flash', def: 'Sieste courte de 15-20 minutes restaurant 2 à 3h de vigilance. Plus longue = inertie de réveil contre-productive.' },
    { term: 'Test de Romberg', def: 'Équilibre statique sur une jambe yeux fermés. Mesure le contrôle cérébeleux, impacté par la fatigue.' },
    { term: 'Effet de seuil', def: 'Au-delà d’un certain niveau de fatigue, le cerveau perd la capacité de l’évaluer lui-même.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "Cet article s'appuie sur l'étude de cas n°22 utilisée dans la formation des moniteurs ECSR : « Formation initiale moto : sensibiliser à la fatigue et ses conséquences ».",
    points: [
      'Public-cible : 6 candidats permis A1/A2 en formation initiale.',
      'Type d’intervention : sensibiliser (aspect physiologique dominant).',
      'Durée : 2 heures en salle, idéalement en début de session matin.',
      "Méthode : test de Romberg en début de cours + analyse de cas réels + atelier 'écoute ton corps'.",
      'Évaluation : engagement écrit personnel sur 3 règles fatigue (sommeil, pauses, bâillements).',
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°22 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-22',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur la fatigue et le sommeil liés à la conduite. Tu dois en réussir <strong>au moins 8 sur 10</strong>.",
    themeCode: 'L',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'alcool-moto',
    number: 4,
    title: '0,2 g/L : pourquoi à moto c\'est différent',
    short: 'Alcool & moto',
    series: 'enquetes',
  },
};

export default fatigueMotoEnquete;
